import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { createColaborador } from '../../services/colaboradorService';
import { extensaoService } from '../../services/extensaoService';
import { fotoService } from '../../services/fotoService';
import ConfirmModal from '../../components/ConfirmModal';
import FotoUpload from '../../components/FotoUpload';
import FakeDataButtons from '../../components/FakeDataButtons';
import useFakeData from '../../hooks/useFakeData';

// Opções para dropdowns
const ESCOLARIDADE_OPTIONS = [
  'Fundamental Incompleto',
  'Fundamental Completo', 
  'Ensino Médio Incompleto',
  'Ensino Médio Completo',
  'Ensino Superior Incompleto',
  'Ensino Superior Completo',
  'Mestrado',
  'Doutorado'
];

const ESTADO_CIVIL_OPTIONS = [
  'Casado(a)',
  'Solteiro(a)',
  'Divorciado(a)',
  'Viúvo(a)',
  'União Estável'
];

const CARGO_OPTIONS = [
  'Pastor(a)',
  'Missionário(a)',
  'Evangelista',
  'Presbítero',
  'Diácono(nisa)',
  'Aux. de Trabalho',
  'Membro',
  'Líder de Dpto',
  'Congregado'
];

const FUNCAO_OPTIONS = [
  'Coordenador(a)',
  'Professor(a)',
  'Secretário(a)'
];

const STATUS_OPTIONS = [
  'Ativo',
  'Inativo'
];

// Função para converter campo boolean que pode vir como buffer do MySQL
const convertBooleanField = (value) => {
  if (value && typeof value === 'object' && value.type === 'Buffer') {
    return value.data[0] === 1;
  } else if (typeof value === 'number') {
    return value === 1;
  } else if (typeof value === 'boolean') {
    return value;
  }
  return false;
};

// Funções para aplicar máscaras
const applyMask = {
  cpf: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers
      .slice(0, 11)
      .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      .replace(/(\d{3})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
      .replace(/(\d{3})(\d{3})(\d{2})/, '$1.$2.$3')
      .replace(/(\d{3})(\d{2})/, '$1.$2');
  },
  
  telefone: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return `(${numbers}`;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      // Telefone fixo: (21)3106-3491
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
    } else {
      // Celular: (21)98029-8201
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  },
  
  // Aplicar máscara de telefone para todos os campos de telefone
  telefoneCelular: function(value) { return this.telefone(value); },
  telefoneResidencial: function(value) { return this.telefone(value); },
  telefoneComercial: function(value) { return this.telefone(value); },
  telefoneIgreja: function(value) { return this.telefone(value); },
  
  cep: (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers
      .slice(0, 8)
      .replace(/(\d{5})(\d{3})/, '$1-$2')
      .replace(/(\d{5})(\d{1,2})/, '$1-$2');
  },
  
  // Aplicar máscara de CEP para os campos de CEP
  cepIgreja: function(value) { return this.cep(value); }
};

const initialForm = {
  nome: '',
  statusAtivo: '',
  cpf: '',
  dataNascimento: '',
  email: '',
  telefoneCelular: '',
  telefoneResidencial: '',
  telefoneComercial: '',
  escolaridade: '',
  estadoCivil: '',
  identidade: '',
  orgaoExpedidor: '',
  cargo: '',
  funcao: '',
  extensao: '',
  estrangeira: false,
  docEstrangeiro: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  pais: '',
  // Campos da igreja
  nomeIgrejaCongrega: '',
  nomePastor: '',
  bairroIgreja: '',
  cidadeIgreja: '',
  estadoIgreja: '',
  cepIgreja: '',
  logradouroIgreja: '',
  numeroIgreja: '',
  telefoneIgreja: '',
  paisIgreja: '',
};

const ColaboradorCreate = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [fotoFile, setFotoFile] = useState(null);
  const [extensoes, setExtensoes] = useState([]);
  const [loadingLists, setLoadingLists] = useState({
    extensoes: false
  });
  const navigate = useNavigate();
  
  // Função para obter classes CSS do campo (com erro ou normal)
  const getFieldClass = (fieldName, baseClass = "w-full border rounded px-3 py-2") => {
    return fieldErrors[fieldName] 
      ? `${baseClass} border-red-500 focus:ring-red-400 bg-red-50`
      : `${baseClass} focus:outline-none focus:ring-2 focus:ring-green-400`;
  };
  
  // Hook para dados fictícios
  const { gerarDadosColaborador, gerarDadosMinimos, gerarDadosInvalidos } = useFakeData();

  // Funções de auto-preenchimento
  const autoPreencherCompleto = () => {
    const dadosFicticios = gerarDadosColaborador();
    setForm(dadosFicticios);
    setError(null);
  };

  const autoPreencherMinimo = () => {
    const dadosMinimos = gerarDadosMinimos();
    setForm(prev => ({ ...prev, ...dadosMinimos }));
    setError(null);
  };

  const autoPreencherTeste = () => {
    const dadosInvalidos = gerarDadosInvalidos();
    setForm(prev => ({ ...prev, ...dadosInvalidos }));
    setError(null);
  };

  // Carregar extensões
  useEffect(() => {
    const loadExtensoes = async () => {
      setLoadingLists(prev => ({ ...prev, extensoes: true }));
      try {
        const response = await extensaoService.list({ limit: 1000 });
        setExtensoes(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar extensões:', error);
      } finally {
        setLoadingLists(prev => ({ ...prev, extensoes: false }));
      }
    };
    loadExtensoes();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Limpar erro do campo específico quando o usuário digitar
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setForm((prev) => {
      const newForm = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Se mudou o estado do checkbox estrangeiro, limpar os campos apropriados
      if (name === 'estrangeira') {
        if (checked) {
          // Se marcou como estrangeiro, limpar CPF
          newForm.cpf = '';
        } else {
          // Se desmarcou estrangeiro, limpar documento estrangeiro
          newForm.docEstrangeiro = '';
        }
      }
      
      return newForm;
    });
  };

  const handleChangeMasked = (e) => {
    const { name, value } = e.target;
    
    // Limpar erro do campo específico quando o usuário digitar
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    const maskedValue = applyMask[name] ? applyMask[name](value) : value;
    setForm(prev => ({ ...prev, [name]: maskedValue }));
  };

  /**
   * Campos obrigatórios marcados com * no formulário. O backend continua
   * validando tudo; isto só evita que o usuário confirme "Deseja realmente
   * cadastrar?" com o formulário incompleto e receba "Dados inválidos" depois.
   * @returns {Record<string,string>}
   */
  const validarCampos = () => {
    const erros = {};
    const obrigatorio = (campo, rotulo) => {
      const valor = (form[campo] || '').toString().trim();
      if (!valor) erros[campo] = `${rotulo} é obrigatório.`;
    };

    obrigatorio('nome', 'Nome');
    obrigatorio('status', 'Status');
    obrigatorio('dataNascimento', 'Data de nascimento');
    obrigatorio('funcao', 'Função');
    obrigatorio('extensao', 'Extensão');

    if (form.estrangeira) {
      obrigatorio('docEstrangeiro', 'Documento estrangeiro');
    } else if (!(form.cpf || '').trim()) {
      erros.cpf = 'CPF é obrigatório.';
    } else if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(form.cpf.trim())) {
      erros.cpf = 'CPF incompleto — informe os 11 dígitos.';
    }

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) erros.email = 'E-mail inválido.';

    return erros;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const erros = validarCampos();
    if (Object.keys(erros).length > 0) {
      setFieldErrors(erros);
      setError('Por favor, corrija os campos destacados em vermelho.');

      const primeiro = document.querySelector(`[name="${Object.keys(erros)[0]}"]`);
      if (primeiro) {
        primeiro.scrollIntoView({ behavior: 'smooth', block: 'center' });
        primeiro.focus();
      }
      return;
    }

    setFieldErrors({});
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    setFieldErrors({});
    setSuccess(false);
    
    try {
      // Preparar dados para envio
      const colaboradorData = {
        ...form,
        estrangeira: Boolean(form.estrangeira), // Garantir que seja booleano
      };
      
      // Limpar campos conforme regra de estrangeiro
      if (colaboradorData.estrangeira) {
        // Se é estrangeiro, limpar CPF
        colaboradorData.cpf = null;
      } else {
        // Se não é estrangeiro, limpar documento estrangeiro
        colaboradorData.docEstrangeiro = null;
      }
      
      console.log('Dados sendo enviados:', colaboradorData);
      
      const response = await createColaborador(colaboradorData);
      
      if (response.success) {
        // Se tem foto, fazer upload
        if (fotoFile && response.data?.idPessoa) {
          try {
            await fotoService.uploadFoto(response.data.idPessoa, fotoFile);
          } catch (fotoError) {
            console.error('Erro ao fazer upload da foto:', fotoError);
            // Não bloqueamos o sucesso do cadastro por erro na foto
          }
        }
        
        setSuccess(true);
        setTimeout(() => navigate('/colaboradores'), 1200);
      } else {
        setError(response.message || 'Erro ao cadastrar colaborador.');
      }
    } catch (err) {
      console.error('Erro ao cadastrar colaborador:', err);
      
      // Processar erros de validação do backend.
      // A lista vem em `validationErrors` (middleware/validation.js); lendo só
      // `errors`, o usuário via apenas "Dados inválidos", sem saber qual dos
      // 35 campos do formulário estava errado.
      const dadosDoErro = err.response?.data;
      const listaDeErros = dadosDoErro?.validationErrors || dadosDoErro?.errors;

      if (Array.isArray(listaDeErros) && listaDeErros.length) {
        const errors = {};
        listaDeErros.forEach(error => {
          const campo = error.path || error.param;
          if (campo && !errors[campo]) {
            errors[campo] = error.msg;
          }
        });
        setFieldErrors(errors);
        setError('Por favor, corrija os campos destacados em vermelho.');
        
        // Scroll para o primeiro campo com erro
        setTimeout(() => {
          const firstErrorField = Object.keys(errors)[0];
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 100);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao cadastrar colaborador.');
      }
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Cadastrar Colaborador</h2>
      {/* Mensagens de Feedback */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <div className="ml-auto">
              <button 
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-green-800 font-medium">Colaborador atualizado com sucesso!</p>
              <p className="text-green-600 text-sm mt-1">Redirecionando para a lista de colaboradores...</p>
            </div>
          </div>
        </div>
      )}        
        {/* Botões de Auto-preenchimento - Comentado para produção */}
        {/* <FakeDataButtons
          onPreencherCompleto={autoPreencherCompleto}
          onPreencherMinimo={autoPreencherMinimo}
          onPreencherTeste={autoPreencherTeste}
          size="sm"
        /> */}
      </div>
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Dados Pessoais */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados Pessoais</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input 
                type="text" 
                name="nome" 
                value={form.nome} 
                onChange={handleChange} 
                maxLength={100}
                className={getFieldClass('nome')}
                placeholder="Nome completo" 
              />
              {fieldErrors.nome && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.nome}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Status *</label>
              <select 
                name="status" 
                value={form.status || ''} 
                onChange={handleChange} 
                className={getFieldClass('status', 'w-full border rounded px-3 py-2')}
              >
                <option value="">Selecione o status</option>
                {STATUS_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {fieldErrors.status && <p className="text-red-600 text-sm mt-1">{fieldErrors.status}</p>}
            </div>
            
            <div className="flex items-center mt-6">
              <input 
                type="checkbox" 
                name="estrangeira" 
                checked={form.estrangeira || false} 
                onChange={handleChange} 
                className="mr-2" 
              />
              <label className="text-sm">Estrangeiro</label>
            </div>
            
            {!form.estrangeira && (
              <div>
                <label className="block text-sm font-medium mb-1">CPF *</label>
                <input 
                  type="text" 
                  name="cpf" 
                  value={form.cpf} 
                  onChange={handleChangeMasked} 
                  maxLength={14}
                  className={getFieldClass('cpf')}
                  placeholder="000.000.000-00" 
                />
                {fieldErrors.cpf && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.cpf}</p>
                )}
              </div>
            )}
            
            {form.estrangeira && (
              <div>
                <label className="block text-sm font-medium mb-1">Documento Estrangeiro *</label>
                <input 
                  type="text" 
                  name="docEstrangeiro" 
                  value={form.docEstrangeiro || ''} 
                  onChange={handleChange} 
                  maxLength={50}
                  className={getFieldClass('docEstrangeiro')}
                  placeholder="Número do documento" 
                />
                {fieldErrors.docEstrangeiro && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.docEstrangeiro}</p>
                )}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-1">Data de Nascimento *</label>
              <input 
                type="date" 
                name="dataNascimento" 
                value={form.dataNascimento} 
                onChange={handleChange}
                className={getFieldClass('dataNascimento')}
              />
              {fieldErrors.dataNascimento && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.dataNascimento}</p>
              )}
            </div>
            <div><label className="block text-sm font-medium mb-1">Email</label><input type="email" name="email" value={form.email} onChange={handleChange} maxLength={60} className="w-full border rounded px-3 py-2" placeholder="Email" /></div>
            <div><label className="block text-sm font-medium mb-1">Telefone Celular</label><input type="text" name="telefoneCelular" value={form.telefoneCelular} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)00000-0000" /></div>
            <div><label className="block text-sm font-medium mb-1">Telefone Residencial</label><input type="text" name="telefoneResidencial" value={form.telefoneResidencial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" /></div>
            <div><label className="block text-sm font-medium mb-1">Telefone Comercial</label><input type="text" name="telefoneComercial" value={form.telefoneComercial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" /></div>
            <div>
              <label className="block text-sm font-medium mb-1">Escolaridade</label>
              <select name="escolaridade" value={form.escolaridade} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione a escolaridade</option>
                {ESCOLARIDADE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado Civil</label>
              <select name="estadoCivil" value={form.estadoCivil} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione o estado civil</option>
                {ESTADO_CIVIL_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Identidade</label><input type="text" name="identidade" value={form.identidade} onChange={handleChange} maxLength={20} className="w-full border rounded px-3 py-2" placeholder="Identidade" /></div>
            <div><label className="block text-sm font-medium mb-1">Órgão Expedidor</label><input type="text" name="orgaoExpedidor" value={form.orgaoExpedidor} onChange={handleChange} maxLength={30} className="w-full border rounded px-3 py-2" placeholder="Órgão Expedidor" /></div>
            <div>
              <label className="block text-sm font-medium mb-1">Cargo</label>
              <select name="cargo" value={form.cargo} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione o cargo</option>
                {CARGO_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Função *</label>
              <select 
                name="funcao" 
                value={form.funcao || ''} 
                onChange={handleChange} 
                className={getFieldClass('funcao', 'w-full border rounded px-3 py-2')}
              >
                <option value="">Selecione a função</option>
                {FUNCAO_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {fieldErrors.funcao && <p className="text-red-600 text-sm mt-1">{fieldErrors.funcao}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Extensão *</label>
              <select 
                name="extensao" 
                value={form.extensao || ''} 
                onChange={handleChange} 
                className={getFieldClass('extensao', 'w-full border rounded px-3 py-2')}
                disabled={loadingLists.extensoes}
              >
                <option value="">Selecione a extensão</option>
                {extensoes.map(extensao => (
                  <option key={extensao.idExtensao} value={extensao.idExtensao}>
                    {extensao.nome}
                  </option>
                ))}
              </select>
              {fieldErrors.extensao && <p className="text-red-600 text-sm mt-1">{fieldErrors.extensao}</p>}
              {loadingLists.extensoes && (
                <span className="text-sm text-gray-500">Carregando extensões...</span>
              )}
            </div>
          </div>
        </div>
        {/* Endereço */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium mb-1">CEP</label><input type="text" name="cep" value={form.cep} onChange={handleChangeMasked} maxLength={9} className="w-full border rounded px-3 py-2" placeholder="00000-000" /></div>
            <div><label className="block text-sm font-medium mb-1">Logradouro</label><input type="text" name="logradouro" value={form.logradouro} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Logradouro" /></div>
            <div><label className="block text-sm font-medium mb-1">Número</label><input type="text" name="numero" value={form.numero} onChange={handleChange} maxLength={30} className="w-full border rounded px-3 py-2" placeholder="Número" /></div>
            <div><label className="block text-sm font-medium mb-1">Complemento</label><input type="text" name="complemento" value={form.complemento} onChange={handleChange} maxLength={255} className="w-full border rounded px-3 py-2" placeholder="Complemento" /></div>
            <div><label className="block text-sm font-medium mb-1">Bairro</label><input type="text" name="bairro" value={form.bairro} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="Bairro" /></div>
            <div><label className="block text-sm font-medium mb-1">Cidade</label><input type="text" name="cidade" value={form.cidade} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Cidade" /></div>
            <div><label className="block text-sm font-medium mb-1">Estado</label><input type="text" name="estado" value={form.estado} onChange={handleChange} maxLength={2} className="w-full border rounded px-3 py-2" placeholder="UF" /></div>
            <div><label className="block text-sm font-medium mb-1">País</label><input type="text" name="pais" value={form.pais} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="País" /></div>
          </div>
        </div>
        
        {/* Upload de Foto */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Foto</h3>
          <FotoUpload
            currentPhotoUrl={null}
            onPhotoChange={(file) => setFotoFile(file)}
            onPhotoRemove={() => setFotoFile(null)}
            label="Foto do Colaborador"
          />
        </div>
        
        {/* Dados Igreja */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados da Igreja</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium mb-1">Nome da Igreja</label><input type="text" name="nomeIgrejaCongrega" value={form.nomeIgrejaCongrega} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Nome da Igreja" /></div>
            <div><label className="block text-sm font-medium mb-1">Nome do Pastor</label><input type="text" name="nomePastor" value={form.nomePastor} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Nome do Pastor" /></div>
            <div><label className="block text-sm font-medium mb-1">Telefone da Igreja</label><input type="text" name="telefoneIgreja" value={form.telefoneIgreja} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(21)98765-4321" /></div>
            <div><label className="block text-sm font-medium mb-1">CEP da Igreja</label><input type="text" name="cepIgreja" value={form.cepIgreja} onChange={handleChangeMasked} maxLength={9} className="w-full border rounded px-3 py-2" placeholder="00000-000" /></div>
            <div><label className="block text-sm font-medium mb-1">Logradouro da Igreja</label><input type="text" name="logradouroIgreja" value={form.logradouroIgreja} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Logradouro da Igreja" /></div>
            <div><label className="block text-sm font-medium mb-1">Número da Igreja</label><input type="text" name="numeroIgreja" value={form.numeroIgreja} onChange={handleChange} maxLength={30} className="w-full border rounded px-3 py-2" placeholder="Número da Igreja" /></div>
            <div><label className="block text-sm font-medium mb-1">Bairro da Igreja</label><input type="text" name="bairroIgreja" value={form.bairroIgreja} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="Bairro da Igreja" /></div>
            <div><label className="block text-sm font-medium mb-1">Cidade da Igreja</label><input type="text" name="cidadeIgreja" value={form.cidadeIgreja} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Cidade da Igreja" /></div>
            <div><label className="block text-sm font-medium mb-1">Estado da Igreja</label><input type="text" name="estadoIgreja" value={form.estadoIgreja} onChange={handleChange} maxLength={2} className="w-full border rounded px-3 py-2" placeholder="UF" /></div>
            <div><label className="block text-sm font-medium mb-1">País da Igreja</label><input type="text" name="paisIgreja" value={form.paisIgreja} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="País da Igreja" /></div>
          </div>
        </div>
        {error && <div className="text-red-600 font-medium mt-2">{error}</div>}
        {success && <div className="text-green-600 font-medium mt-2">Colaborador cadastrado com sucesso!</div>}
        <div className="flex gap-4 mt-4 justify-end">
          <button type="button" className="px-4 py-2 rounded bg-gray-400 text-white font-semibold shadow hover:bg-gray-500 focus:outline-none flex items-center gap-2" onClick={() => navigate('/colaboradores')}>
            <span className="block sm:hidden"><FontAwesomeIcon icon={faTimes} /></span>
            <span className="hidden sm:block"><FontAwesomeIcon icon={faTimes} /> Cancelar</span>
          </button>
          <button type="submit" className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2" disabled={loading}>
            <span className="block sm:hidden"><FontAwesomeIcon icon={faSave} /></span>
            <span className="hidden sm:block"><FontAwesomeIcon icon={faSave} /> Salvar</span>
          </button>
        </div>
      </form>
      <ConfirmModal isOpen={showConfirm} onConfirm={handleConfirm} onCancel={() => setShowConfirm(false)} title="Confirmação" message="Deseja realmente cadastrar este colaborador?" />
    </div>
  );
};

export default ColaboradorCreate;
